import React, { useState, useEffect } from 'react'
import SpaceTheme from '../components/SpaceTheme'
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Chip,
  Divider
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  Assessment as AssessmentIcon,
  Group as GroupIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material'
import { useAuth } from '../hooks/useAuth'
import { db } from '../firebase/config'
import { collection, query, where, getDocs, deleteDoc, doc, writeBatch } from 'firebase/firestore'
import toast from 'react-hot-toast'

const QuizResults = ({ userData, onBack }) => {
  const { user } = useAuth()
  const [loading, setLoading] = useState(false)
  const [quizResults, setQuizResults] = useState([])
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [selectedQuiz, setSelectedQuiz] = useState(null)

  useEffect(() => {
    if (user) {
      loadTeacherQuizResults()
    }
  }, [user])

  const loadTeacherQuizResults = async () => {
    try {
      setLoading(true)
      const teacherId = user.uid
      
      // Öğretmenin quiz'lerini yükle
      const quizzesRef = collection(db, 'miniQuizzes')
      const q = query(quizzesRef, where('teacherId', '==', teacherId))
      const querySnapshot = await getDocs(q)
      
      const tempResults = []
      
      for (const quizDoc of querySnapshot.docs) {
        const quizData = quizDoc.data()
        
        const quizResult = {
          quizId: quizDoc.id,
          quizTitle: quizData.title || 'Quiz',
          quizDescription: quizData.description || '',
          createdDate: quizData.createdDate?.toDate() || new Date(),
          studentIds: quizData.studentIds || [],
          studentNames: quizData.studentNames || [],
          questions: quizData.questions || [],
          studentResults: []
        }
        
        // Her quiz için öğrenci sonuçlarını yükle
        await loadStudentResultsForQuiz(quizResult)
        tempResults.push(quizResult)
      }
      
      // Quiz'leri oluşturulma tarihine göre sırala (en yeni en üstte)
      tempResults.sort((a, b) => b.createdDate - a.createdDate)
      
      setQuizResults(tempResults)
    } catch (error) {
      console.error('Error loading teacher quizzes:', error)
      toast.error('Quiz\'ler yüklenirken hata oluştu')
    } finally {
      setLoading(false)
    }
  }

  const loadStudentResultsForQuiz = async (quizResult) => {
    try {
      const resultsRef = collection(db, 'quizResults')
      const q = query(
        resultsRef, 
        where('quizId', '==', quizResult.quizId),
        where('teacherId', '==', user.uid)
      )
      const querySnapshot = await getDocs(q)
      
      for (const resultDoc of querySnapshot.docs) {
        const resultData = resultDoc.data()
        
        const studentResult = {
          studentId: resultData.studentId,
          studentName: resultData.studentName || 'Bilinmeyen Öğrenci',
          score: resultData.score || 0,
          totalQuestions: resultData.totalQuestions || 0,
          completionDate: resultData.completionDate?.toDate() || new Date(),
          userAnswers: resultData.userAnswers || []
        }
        
        quizResult.studentResults.push(studentResult)
      }
    } catch (error) {
      console.error('Error loading student results for quiz:', error)
    }
  }

  const handleViewDetails = (quiz) => {
    // Quiz detay sayfasına yönlendir
    onBack({ 
      page: 'quiz-detail', 
      quizId: quiz.quizId, 
      quizTitle: quiz.quizTitle 
    })
  }

  const handleDeleteQuiz = (quiz) => {
    setSelectedQuiz(quiz)
    setShowDeleteDialog(true)
  }

  const confirmDeleteQuiz = async () => {
    if (!selectedQuiz) return
    
    try {
      setLoading(true)
      
      // Önce quiz sonuçlarını sil
      const resultsRef = collection(db, 'quizResults')
      const q = query(
        resultsRef, 
        where('quizId', '==', selectedQuiz.quizId),
        where('teacherId', '==', user.uid)
      )
      const querySnapshot = await getDocs(q)
      
      const batch = writeBatch(db)
      
      // Quiz sonuçlarını sil
      querySnapshot.docs.forEach((doc) => {
        batch.delete(doc.ref)
      })
      
      // Quiz'i sil
      const quizRef = doc(db, 'miniQuizzes', selectedQuiz.quizId)
      batch.delete(quizRef)
      
      await batch.commit()
      
      toast.success('Quiz başarıyla silindi')
      setShowDeleteDialog(false)
      setSelectedQuiz(null)
      loadTeacherQuizResults() // Listeyi yenile
    } catch (error) {
      console.error('Error deleting quiz:', error)
      toast.error('Quiz silinirken hata oluştu')
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (date) => {
    if (!date) return '-'
    return new Intl.DateTimeFormat('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date)
  }

  return (
    <SpaceTheme>
      <Box sx={{ flexGrow: 1 }}>
      {/* App Bar */}
      <AppBar position="static" sx={{ 
        background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
      }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onBack}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Quiz Sonuçları
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="md" sx={{ py: 3 }}>
        {loading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>Yükleniyor...</Typography>
          </Box>
        ) : quizResults.length === 0 ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            Henüz quiz oluşturmamışsınız
          </Alert>
        ) : (
          <List>
            {quizResults.map((quiz, index) => (
              <Card key={quiz.quizId} sx={{ mb: 2, borderRadius: 2 }}>
                <CardContent sx={{ p: 3 }}>
                  {/* Quiz Başlığı ve Butonlar */}
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <Typography variant="h6" fontWeight="bold" color="primary">
                      {quiz.quizTitle}
                    </Typography>
                    <Box display="flex" gap={1}>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<VisibilityIcon />}
                        onClick={() => handleViewDetails(quiz)}
                        sx={{ minWidth: 'auto', px: 2 }}
                      >
                        Detay
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleDeleteQuiz(quiz)}
                        sx={{ minWidth: 'auto', px: 2 }}
                      >
                        Sil
                      </Button>
                    </Box>
                  </Box>

                  {/* Quiz Açıklaması */}
                  {quiz.quizDescription && (
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      {quiz.quizDescription}
                    </Typography>
                  )}

                  {/* Quiz İstatistikleri */}
                  <Box display="flex" flexWrap="wrap" gap={2} mb={2}>
                    <Chip
                      icon={<GroupIcon />}
                      label={`Toplam Öğrenci: ${quiz.studentIds.length}`}
                      variant="outlined"
                      color="primary"
                    />
                    <Chip
                      icon={<AssessmentIcon />}
                      label={`Tamamlayan: ${quiz.studentResults.length}`}
                      variant="outlined"
                      color="success"
                    />
                    <Chip
                      icon={<ScheduleIcon />}
                      label={`Oluşturulma: ${formatDate(quiz.createdDate)}`}
                      variant="outlined"
                      color="info"
                    />
                  </Box>

                  {/* Öğrenci Sonuçları Özeti */}
                  {quiz.studentResults.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold" mb={1}>
                        Sonuçlar:
                      </Typography>
                      <Box display="flex" flexWrap="wrap" gap={1}>
                        {quiz.studentResults.slice(0, 3).map((result, idx) => (
                          <Chip
                            key={result.studentId}
                            label={`${result.studentName}: ${result.score}/${result.totalQuestions}`}
                            size="small"
                            color={result.score === result.totalQuestions ? 'success' : 'default'}
                          />
                        ))}
                        {quiz.studentResults.length > 3 && (
                          <Chip
                            label={`+${quiz.studentResults.length - 3} daha`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </Card>
            ))}
          </List>
        )}
      </Container>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Quiz'i Sil</DialogTitle>
        <DialogContent>
          <Typography>
            '{selectedQuiz?.quizTitle}' quiz'ini silmek istediğinizden emin misiniz? 
            Bu işlem geri alınamaz ve tüm quiz sonuçları da silinecektir.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)}>
            İptal
          </Button>
          <Button 
            onClick={confirmDeleteQuiz} 
            color="error" 
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : 'Sil'}
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </SpaceTheme>
  )
}

export default QuizResults
